import styles from "./CompanySmsSendingRuleRowV2.module.scss"
import { formatManuallyClaimingConversion } from "./CompanySmsSendingRuleV2.const"

import {
	FragmentType,
	getFragmentData,
	graphql,
} from "@/souhl_admin/gql/__generated__"
import { useCancelTypeTexts } from "@/souhl_admin/lib/useCancelTypeTexts"

import {
	convertUnitMinVisitCountsToSegments,
	formatUnitVisitCounts,
	getManuallyClaimingUnitSegmentColor,
	getUnitSegmentColor,
} from "../EncouragingRereservationUnitsSlider/EncouragingRereservationUnitsSlider"
import { FunctionVisitCountsSlider } from "../FunctionVisitCountsSlider/FunctionVisitCountsSlider"
import { isClaimingFunctionEnabled } from "../companySmsSendingRuleEditV2/logic"

const CompanySmsSendingRuleRowV2Fragment = graphql(/* GraphQL */ `
	fragment CompanySmsSendingRuleRowV2Fragment on CompanySmsSendingRuleV2CancelTypeSetting {
		cancelFeeAmountForNonZeroYenReservation
		cancelFeeAmountForZeroYenReservation
		cancelFeePercentForNonZeroYenReservation
		cancelType
		claimingConversionPolicyName
		encouragingRereservationPolicyName
		encouragingRereservationUnits {
			minVisitCount
			encouragingRereservationPolicyName
		}
		isClaimableCancelType
		manuallyClaimingUnits {
			minVisitCount
			manuallyClaimingUnitKind
			conversionDays
		}
		minVisitCountForClaimingConversion
		minVisitCountForEncouragingRereservation
		minVisitCountForManuallyClaimingCancelFee
		minVisitCountForNothing
	}
`)

const noopValuesChange = (_values: number[]) => undefined

export const CompanySmsSendingRuleRowV2: React.FC<{
	row: FragmentType<typeof CompanySmsSendingRuleRowV2Fragment>
}> = ({ row }) => {
	const { getCancelTypeLabel } = useCancelTypeTexts()
	const {
		cancelFeeAmountForNonZeroYenReservation,
		cancelFeeAmountForZeroYenReservation,
		cancelFeePercentForNonZeroYenReservation,
		cancelType,
		claimingConversionPolicyName,
		encouragingRereservationPolicyName,
		encouragingRereservationUnits,
		manuallyClaimingUnits,
		minVisitCountForClaimingConversion,
		minVisitCountForEncouragingRereservation,
		minVisitCountForManuallyClaimingCancelFee,
		minVisitCountForNothing,
	} = getFragmentData(CompanySmsSendingRuleRowV2Fragment, row)

	const isClaimingEnabled = isClaimingFunctionEnabled(
		minVisitCountForEncouragingRereservation
	)

	return (
		<div className={styles.company_sms_sending_rule_row}>
			<div className={styles.row_header}>{getCancelTypeLabel(cancelType)}</div>
			<div className={styles.row_container}>
				<div className={styles.rule_header}>
					<div className={styles.rule_cell}>請求基準</div>
					<div className={styles.rule_cell}>請求金額(予約金額あり)</div>
					<div className={styles.rule_cell}>請求金額(予約金額未定)</div>
					<div className={styles.rule_cell}>請求転換種別</div>
					<div className={styles.rule_cell}>手動請求種別</div>
					<div className={styles.rule_cell}>再予約案内種別</div>
				</div>
				<div className={styles.company_sms_sending_rule_unit}>
					<div>
						<FunctionVisitCountsSlider
							values={[
								minVisitCountForClaimingConversion,
								minVisitCountForManuallyClaimingCancelFee,
								minVisitCountForEncouragingRereservation,
								minVisitCountForNothing,
							]}
							onValuesChange={noopValuesChange}
							disabled
						/>
					</div>
					<div className={styles.rule_cell}>
						{cancelFeePercentForNonZeroYenReservation != null && (
							<div>{cancelFeePercentForNonZeroYenReservation}％</div>
						)}
						{cancelFeeAmountForNonZeroYenReservation != null && (
							<div>{cancelFeeAmountForNonZeroYenReservation}円</div>
						)}
						{cancelFeePercentForNonZeroYenReservation === null &&
							cancelFeeAmountForNonZeroYenReservation === null && <div>-</div>}
					</div>
					<div className={styles.rule_cell}>
						{isClaimingEnabled ? (
							<>{cancelFeeAmountForZeroYenReservation}円</>
						) : (
							<>-</>
						)}
					</div>
					<div className={styles.rule_cell}>
						{claimingConversionPolicyName || "-"}
					</div>
					<div className={styles.rule_cell}>
						{manuallyClaimingUnits.length > 0 ? (
							<div className={styles.encouraging_rereservation_units}>
								{convertUnitMinVisitCountsToSegments(
									manuallyClaimingUnits.map((unit) => unit.minVisitCount),
									minVisitCountForEncouragingRereservation
								).map((segment) => (
									<div
										key={segment.start}
										className={styles.encouraging_rereservation_unit}
									>
										<span
											className={styles.encouraging_rereservation_unit_color}
											style={{
												backgroundColor: getManuallyClaimingUnitSegmentColor(
													segment.index
												),
											}}
										/>
										{formatUnitVisitCounts(segment)}:{" "}
										{formatManuallyClaimingConversion(
											manuallyClaimingUnits[segment.index]
										)}
									</div>
								))}
							</div>
						) : (
							"-"
						)}
					</div>
					<div className={styles.rule_cell}>
						{encouragingRereservationUnits.length > 0 ? (
							<div className={styles.encouraging_rereservation_units}>
								{convertUnitMinVisitCountsToSegments(
									encouragingRereservationUnits.map(
										(unit) => unit.minVisitCount
									),
									minVisitCountForNothing
								).map((segment) => (
									<div
										key={segment.start}
										className={styles.encouraging_rereservation_unit}
									>
										<span
											className={styles.encouraging_rereservation_unit_color}
											style={{
												backgroundColor: getUnitSegmentColor(segment.index),
											}}
										/>
										{formatUnitVisitCounts(segment)}:{" "}
										{
											encouragingRereservationUnits[segment.index]
												.encouragingRereservationPolicyName
										}
									</div>
								))}
							</div>
						) : (
							encouragingRereservationPolicyName || "-"
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
